/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import React from 'react';
import Logo from '@/components/atoms/logo/logo';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import ProductCard, {
  ProductCardProps,
} from '@/components/organisms/product-card/product-card';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { getLogoName } from '@/libraries/getters/get-logo-name';
import styles from './favorites-card-list.module.scss';

export interface FavoritesCardListProps {
  favorites: ProductCardProps[];
  isLoading?: boolean;
  onClickFavorite?: (offerId: string) => void;
}

const FavoritesCardList = ({
  favorites = [],
  isLoading = false,
  onClickFavorite,
}: FavoritesCardListProps) => {
  const { t } = useI18n();
  const { brand } = usePageDetailsContext();

  const renderNoResults = () => {
    return (
      <div className={styles.noResultsContainer}>
        <Logo
          className={styles.noResultsIcon}
          name={getLogoName({
            brand: brand,
            type: 'NoFavorites',
          })}
        />

        <div className={styles.noResultsContent}>
          <Typography
            className={styles.noResultsHeading}
            tag="h2"
            tagStyle="headingMedium"
          >
            {t('items.favorites.noFavorites.header')}
          </Typography>

          <Typography
            className={styles.noResultsText}
            tag="span"
            tagStyle="bodyMedium"
          >
            {t('items.favorites.noFavorites.text')}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <div className={classNames(styles.component)}>
      {favorites.length === 0 && !isLoading && renderNoResults()}

      {isLoading ? (
        <React.Fragment>
          <SkeletonLoader borderRadius="4px" height="25px" width="30%" />

          <div className={styles.resultsList}>
            {Array.from({ length: 4 }).map((_, loaderIndex) => (
              <ProductCard key={loaderIndex} isLoading />
            ))}
          </div>
        </React.Fragment>
      ) : (
        <React.Fragment>
          {favorites.length > 0 && (
            <React.Fragment>
              <Typography
                className={styles.heading}
                tag="h2"
                tagStyle="headingSmall"
              >
                {`${favorites.length} ${t('items.favorites.favorites')}`}
              </Typography>

              <div className={styles.resultsList}>
                {favorites.map(result => (
                  <ProductCard
                    key={result.offerId}
                    {...result}
                    onClickFavorite={() =>
                      onClickFavorite?.(result.uniqueId as string)
                    }
                  />
                ))}
              </div>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

FavoritesCardList.displayName = 'FavoritesCardList';

export default FavoritesCardList;
